/**
 * CmsDirectoryDedup — audit remediation Phase 3.
 *
 * Surfaces `/api/admin/directory/duplicates` inside the CMS with a
 * one-click "keep this one, merge the others" workflow. Every group
 * shares the same normalized name+city+state key; the admin picks
 * which row to keep and clicks Merge — the others get marked
 * `status="merged"` and drop out of every public listing.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Loader2, ShieldCheck, MergeIcon, Search, RefreshCw, CheckCircle2, MapPin,
} from "lucide-react";

export default function CmsDirectoryDedup() {
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ group_count: 0, total_dup_rows: 0 });
  const [loading, setLoading] = useState(true);
  const [busyGroup, setBusyGroup] = useState(null);
  const [keepFor, setKeepFor] = useState({}); // groupKey → keep_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/directory/duplicates", { params: { limit: 50 } });
      const gs = r.data?.groups || [];
      setGroups(gs);
      setMeta({
        group_count: r.data?.group_count || 0,
        total_dup_rows: r.data?.total_dup_rows || 0,
      });
      // Preselect the first row (usually the highest-verified) as "keep"
      const nextKeep = {};
      gs.forEach((g, i) => {
        const key = `${i}-${g._id?.n || ""}`;
        if (g.rows?.length) nextKeep[key] = g.rows[0].id;
      });
      setKeepFor(nextKeep);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load dedup queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runMerge = async (groupIdx, groupKey, group) => {
    const keep = keepFor[groupKey];
    if (!keep) { toast.error("Pick a row to keep first"); return; }
    const merge_ids = group.rows.filter((r) => r.id !== keep).map((r) => r.id);
    if (merge_ids.length === 0) { toast.error("Nothing to merge"); return; }
    setBusyGroup(groupIdx);
    try {
      const r = await http.post("/admin/directory/duplicates/merge", {
        keep_id: keep,
        merge_ids,
      });
      if (r.data?.ok) {
        toast.success(`Merged ${r.data.merged} row${r.data.merged === 1 ? "" : "s"} into "${r.data.keep_name}"`);
        // Remove the group locally so the list shrinks instantly.
        setGroups((gs) => gs.filter((_, i) => i !== groupIdx));
        setMeta((m) => ({
          group_count: Math.max(0, m.group_count - 1),
          total_dup_rows: Math.max(0, m.total_dup_rows - group.rows.length),
        }));
      } else {
        toast.error(r.data?.error || "Merge failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setBusyGroup(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="cms-directory-dedup">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" /> Directory dedup queue
          </div>
          <h1 className="mt-1 font-serif text-2xl">Merge duplicate businesses</h1>
          <p className="mt-1 text-xs text-slate-500">
            Rows sharing a normalized <b>name + city + state</b> key. Pick the row to keep — the others will be marked <code>merged</code> and hidden from public listings.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="cms-dedup-reload"
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Reload
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="cms-dedup-summary">
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Duplicate groups</div>
          <div className="mt-0.5 text-xl font-semibold text-slate-900">{meta.group_count}</div>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Duplicate rows</div>
          <div className="mt-0.5 text-xl font-semibold text-rose-600">{meta.total_dup_rows}</div>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Would clean up</div>
          <div className="mt-0.5 text-xl font-semibold text-emerald-700">
            {Math.max(0, meta.total_dup_rows - meta.group_count)}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading duplicate groups…
        </div>
      ) : groups.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-emerald-200 bg-emerald-50/60 p-6 text-center text-sm text-emerald-900" data-testid="cms-dedup-empty">
          <CheckCircle2 className="mx-auto mb-2 h-5 w-5 text-emerald-600" />
          Clean directory — no obvious duplicates on the {`{name, city, state}`} key.
        </div>
      ) : (
        <ul className="mt-6 space-y-4" data-testid="cms-dedup-groups">
          {groups.map((g, i) => {
            const groupKey = `${i}-${g._id?.n || ""}`;
            const keep = keepFor[groupKey];
            const rows = g.rows || [];
            return (
              <li key={groupKey} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-dedup-group-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {rows[0]?.name || "—"}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <MapPin className="h-3 w-3" />
                      {(rows[0]?.city || "—")}{rows[0]?.state ? `, ${rows[0].state}` : ""}
                      <span className="text-slate-300">·</span>
                      <span>{rows.length} rows</span>
                    </div>
                  </div>
                  <button
                    onClick={() => runMerge(i, groupKey, g)}
                    disabled={busyGroup === i}
                    data-testid={`cms-dedup-merge-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {busyGroup === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <MergeIcon className="h-3 w-3" />}
                    Merge into selected
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {rows.map((r) => {
                    const isKeep = r.id === keep;
                    return (
                      <label
                        key={r.id}
                        className={`flex items-start gap-2 rounded-lg border p-2 text-xs cursor-pointer transition-colors ${
                          isKeep ? "border-emerald-300 bg-emerald-50" : "border-slate-100 bg-white hover:bg-slate-50"
                        }`}
                        data-testid={`cms-dedup-row-${r.id}`}
                      >
                        <input
                          type="radio"
                          name={`keep-${groupKey}`}
                          checked={isKeep}
                          onChange={() => setKeepFor((prev) => ({ ...prev, [groupKey]: r.id }))}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-slate-900">{r.name}</div>
                          <div className="flex flex-wrap items-center gap-1 text-[10px] text-slate-500">
                            <span>{r.type || "—"}</span>
                            {r.verified && (<><span className="text-slate-300">·</span><span className="text-emerald-700">verified</span></>)}
                            <span className="text-slate-300">·</span>
                            <code className="text-slate-400">{r.id?.slice(0, 8)}</code>
                          </div>
                        </div>
                        {isKeep && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Keep</span>}
                      </label>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
